import { useEffect, useMemo, useState } from 'react';
import {
  Stack, Table, Paper, Group, Button, Text, Select, TextInput, Badge, Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { useNavigate } from 'react-router-dom';
import { IconAdd as IconPlus, IconClipboard, IconScope } from '../../components/icons/CncIcons';
import { api, imageUrl, downloadFile } from '../../api/client';
import { useAuth, isAdmin } from '../../store/auth';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';

type OrderState = 'FINALIZED' | 'TECHNICAL_DIFFICULTY' | 'ONGOING' | 'CANCELLED';
type OrderStatus = 'Order Set-up' | 'In Quotation' | 'Supplying Consumables' | 'In Execution' | 'Preparing to Ship' | 'Shipped' | 'Delivered';

export interface OrderRow {
  id: number;
  orderUid: string;
  customerId: number | null;
  customerName: string | null;
  customerLogo: string | null;
  contactId: number | null;
  contactName: string | null;
  technicianId: number | null;
  technicianName: string | null;
  name: string;
  notes: string | null;
  imagePath: string | null;
  orderDate: string;
  state: OrderState;
  status: OrderStatus;
  quotedPrice: number | null;
  calculatedCost: number | null;
}

const STATE_COLOR: Record<OrderState, string> = {
  FINALIZED: '#7a7a7a',          // grey
  ONGOING: 'var(--mantine-color-green-6)',
  TECHNICAL_DIFFICULTY: 'var(--mantine-color-red-6)',
  CANCELLED: '#000',
};

function StateDot({ state }: { state: OrderState }) {
  return (
    <Tooltip label={state.replace('_', ' ')} withArrow>
      <span
        style={{
          display: 'inline-block',
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: STATE_COLOR[state],
          border: '1px solid rgba(0,0,0,0.15)',
        }}
      />
    </Tooltip>
  );
}

type SortKey = 'state' | 'status' | 'orderUid' | 'name' | 'customerName' | 'quotedPrice' | 'calculatedCost' | 'technicianName' | 'orderDate';

export default function OrdersPage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const admin = isAdmin(me?.role);
  const [rows, setRows] = useState<OrderRow[]>([]);
  const [q, setQ] = useState('');
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('orderDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  async function refresh() {
    const list = await api.get<OrderRow[]>('/orders');
    setRows(list);
  }
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (stateFilter && r.state !== stateFilter) return false;
      if (statusFilter && r.status !== statusFilter) return false;
      if (needle) {
        const hay = [r.orderUid, r.name, r.customerName, r.technicianName, r.status].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
    out = out.slice().sort((a, b) => {
      const av: any = (a as any)[sortKey] ?? '';
      const bv: any = (b as any)[sortKey] ?? '';
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return out;
  }, [rows, q, stateFilter, statusFilter, sortKey, sortDir]);

  function onSort(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('asc'); }
  }

  const [trackerBusy, setTrackerBusy] = useState(false);
  const [zipBusyId, setZipBusyId] = useState<number | null>(null);

  async function downloadTracker() {
    setTrackerBusy(true);
    try {
      await downloadFile('/exports/order-tracker.xlsx', 'dProto_OrderTracker.xlsx');
    } catch (e: any) {
      notifications.show({ message: `Order Tracker download failed: ${e.message ?? e}`, color: 'red' });
    } finally {
      setTrackerBusy(false);
    }
  }

  async function downloadFolderZip(r: OrderRow, e: React.MouseEvent) {
    e.stopPropagation();
    setZipBusyId(r.id);
    try {
      await downloadFile(`/exports/orders/${r.id}/folder-zip`, `${r.orderUid}.zip`);
    } catch (err: any) {
      notifications.show({ message: `Folder ZIP download failed: ${err.message ?? err}`, color: 'red' });
    } finally {
      setZipBusyId(null);
    }
  }

  function SortableTh({ k, children, w }: { k: SortKey; children: React.ReactNode; w?: number }) {
    const active = sortKey === k;
    return (
      <Table.Th style={{ cursor: 'pointer', width: w }} onClick={() => onSort(k)}>
        {children} {active ? (sortDir === 'asc' ? '▲' : '▼') : ''}
      </Table.Th>
    );
  }

  function fatalDelete(r: OrderRow, e: React.MouseEvent) {
    e.stopPropagation();
    modals.openConfirmModal({
      title: `Fatal Delete — ${r.orderUid}?`,
      children: (
        <Text size="sm">
          This will <strong>permanently delete</strong> order <code>{r.orderUid}</code> ("{r.name}") and all
          its parts, quotations, step actuals, and files. This cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete forever', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.delete(`/orders/${r.id}`);
          await refresh();
        } catch (err: any) {
          modals.open({
            title: 'Delete failed',
            children: <Text size="sm">{err.message ?? 'Unknown error'}</Text>,
          });
        }
      },
    });
  }

  return (
    <Stack>
      <Group justify="space-between" align="center">
        <Group gap={10}>
          <IconClipboard size={26} />
          <Text fw={700} fz="h2">Orders</Text>
          <Button
            variant="light"
            size="compact-sm"
            onClick={downloadTracker}
            loading={trackerBusy}
            title="Download the SharePoint Order Tracker (dProto_OrderTracker.xlsx) for all orders"
            data-tour="orders-tracker"
          >
            Order Tracker (.xlsx)
          </Button>
        </Group>
        <Group gap="xs">
          <TextInput
            placeholder="Search…"
            leftSection={<IconScope size={14} />}
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            w={220}
          />
          <Select
            placeholder="All states"
            data={['ONGOING', 'TECHNICAL_DIFFICULTY', 'FINALIZED', 'CANCELLED']}
            value={stateFilter}
            onChange={setStateFilter}
            clearable
            w={180}
          />
          <Select
            placeholder="All statuses"
            data={['Order Set-up', 'In Quotation', 'Supplying Consumables', 'In Execution', 'Preparing to Ship', 'Shipped', 'Delivered']}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={200}
          />
        </Group>
      </Group>

      <Paper withBorder>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh k="state" w={70}>State</SortableTh>
              <SortableTh k="status" w={160}>Status</SortableTh>
              <Table.Th style={{ width: 64 }}>Image</Table.Th>
              <SortableTh k="orderUid" w={120}>Order ID</SortableTh>
              <SortableTh k="name">Name</SortableTh>
              <SortableTh k="customerName" w={180}>Customer</SortableTh>
              <SortableTh k="quotedPrice" w={120}>Quoted</SortableTh>
              <SortableTh k="calculatedCost" w={120}>Cost</SortableTh>
              <SortableTh k="technicianName" w={160}>Technician</SortableTh>
              <SortableTh k="orderDate" w={150}>Date</SortableTh>
              <Table.Th style={{ width: 130 }}>SharePoint</Table.Th>
              {admin && <Table.Th style={{ width: 120 }}>Admin</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.map((r) => (
              <Table.Tr key={r.id} className="click-row" onClick={() => navigate(`/orders/${r.id}`)} data-tour="order-row">
                <Table.Td><StateDot state={r.state} /></Table.Td>
                <Table.Td><Badge variant="light" color="gray">{r.status}</Badge></Table.Td>
                <Table.Td>
                  <div className="image-box">
                    {r.imagePath && <img src={imageUrl(r.imagePath, true)} alt={r.name} />}
                  </div>
                </Table.Td>
                <Table.Td><code>{r.orderUid}</code></Table.Td>
                <Table.Td>{r.name}</Table.Td>
                <Table.Td>{r.customerName ?? <Text span c="dimmed">—</Text>}</Table.Td>
                <Table.Td>{r.quotedPrice != null ? `€${r.quotedPrice.toFixed(2)}` : <Text span c="dimmed">—</Text>}</Table.Td>
                <Table.Td>{r.calculatedCost != null ? `€${r.calculatedCost.toFixed(2)}` : <Text span c="dimmed">—</Text>}</Table.Td>
                <Table.Td>{r.technicianName ?? <Text span c="dimmed">—</Text>}</Table.Td>
                <Table.Td>{dayjs(r.orderDate).format('YYYY-MM-DD HH:mm')}</Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="compact-xs"
                    variant="light"
                    loading={zipBusyId === r.id}
                    onClick={(e) => downloadFolderZip(r, e)}
                    title="Download this order's SharePoint folder structure (.zip)"
                    data-tour="order-folder-zip"
                  >
                    Folder .zip
                  </Button>
                </Table.Td>
                {admin && (
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="compact-xs"
                      color="red"
                      variant="light"
                      onClick={(e) => fatalDelete(r, e)}
                      data-tour="order-fatal-delete"
                    >
                      Fatal Delete
                    </Button>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
            <Table.Tr>
              <Table.Td colSpan={admin ? 12 : 11}>
                <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} onClick={() => navigate('/orders/new')} data-tour="orders-add">
                  Add Order
                </Button>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Paper>
    </Stack>
  );
}
